// API
import InventoryApi from '@/api/InventoryApi';

// Utils
import AudioUtil, { soundNames } from '@/utils/AudioUtil';
import ErrorUtil from '@/utils/ErrorUtil';
import ScanUtil from '@/utils/ScanUtil';
import DateUtil from '@/utils/DateUtil';

export const mutationTypes = {
    SET_DESTINATION_LOCATION: 'SET_DESTINATION_LOCATION',
    SET_EXPECTED_RECEIVE_DATE: 'SET_EXPECTED_RECEIVE_DATE',
    SET_SCAN_STRING: 'SET_SCAN_STRING',
    SET_SCANNED_ITEM: 'SET_SCANNED_ITEM',
    PUSH_SCANNED_ITEM: 'PUSH_SCANNED_ITEM',
    REMOVE_SCANNED_ITEM: 'REMOVE_SCANNED_ITEM',
    RESET_SCANNED_ITEMS: 'RESET_SCANNED_ITEMS',
    SET_IN_FLIGHT: 'SET_IN_FLIGHT',
};

export const actionTypes = {
    completePartnerShip: 'completePartnerShip',
    processScan: 'processScan',
    verifyAndProcessScan: 'verifyAndProcessScan',
    verifyScan: 'verifyScan',
    getLocations: 'getLocations',
    reset: 'reset',
};

export default {
    namespaced: true,
    state: {
        /** @type { Object } */
        destinationLocation: {},
        /** @type { String } */
        expectedReceiveDate: DateUtil.formatDate( Date.now() ),
        /** @type { String } */
        scanString: '',
        /** @type { Object[] } */
        scannedItems: [],
        /** @type { Boolean } */
        inFlight: false,
    },
    actions: {
        [ actionTypes.reset ]( { commit } ) {
            commit( mutationTypes.SET_DESTINATION_LOCATION, { destinationLocation: {} } );
            commit( mutationTypes.SET_EXPECTED_RECEIVE_DATE, { expectedReceiveDate: DateUtil.formatDate( Date.now() ) } );
            commit( mutationTypes.SET_SCAN_STRING, { scanString: '' } );
            commit( mutationTypes.SET_IN_FLIGHT, { inFlight: false } );
            commit( mutationTypes.RESET_SCANNED_ITEMS );
        },

        async [ actionTypes.completePartnerShip ]( context ) {
            try {
                context.commit( mutationTypes.SET_IN_FLIGHT, { inFlight: true } );
                await InventoryApi.postCompletePartnerShip( {
                    userAccountId: context.rootState.User.accountId,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    jwtToken: context.rootState.User.jwtToken,
                    completeBody: {
                        destinationLocationId: context.state.destinationLocation.id,
                        serialItemRefNums: context.state.scannedItems.map( ( scannedItem ) => scannedItem.serial.substring( 2 ) ),
                        expectedReceiveDate: context.state.expectedReceiveDate,
                    },
                } );
                await AudioUtil.playSound( soundNames.SUCCESS_LONG );
                return true;
            } catch ( e ) {
                await context.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( e ),
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                await AudioUtil.playSound( soundNames.FAILURE_LONG );
                return false;
            } finally {
                context.commit( mutationTypes.SET_IN_FLIGHT, { inFlight: false } );
            }
        },
        async [ actionTypes.verifyScan ]( {
            rootState,
            rootGetters,
        }, scan ) {
            if ( !ScanUtil.isSerialRef( scan ) ) {
                throw new Error( 'Not a serial ID' );
            }
            await InventoryApi.getValidateItemForPartnerShip( {
                userAccountId: rootState.User.accountId,
                websiteId: rootGetters[ 'Website/id' ],
                jwtToken: rootState.User.jwtToken,
                code: scan,
                locationId: rootGetters[ 'Website/locationId' ],
            } );
        },
        async [ actionTypes.processScan ]( {
            commit,
            rootState,
            rootGetters,
        }, scan ) {
            const { data } = await InventoryApi.getSerialItemInfo( {
                userAccountId: rootState.User.accountId,
                websiteId: rootGetters[ 'Website/id' ],
                jwtToken: rootState.User.jwtToken,
                ref: scan,
            } );
            const item = data.serialItems.list[ 0 ];
            commit( mutationTypes.SET_SCANNED_ITEM, {
                serial: item.ref,
                title: item.titleDescription,
                isbn: item.ean,
                status: 'complete',
            } );
        },
        async [ actionTypes.verifyAndProcessScan ]( {
            dispatch,
            commit,
            state,
        } ) {
            const scan = state.scanString;
            commit( mutationTypes.SET_SCAN_STRING, { scanString: '' } );
            if ( ScanUtil.hasItem( scan, state.scannedItems ) ) {
                commit( mutationTypes.SET_SCANNED_ITEM, {
                    serial: scan, status: 'loading',
                } );
            } else {
                commit( mutationTypes.PUSH_SCANNED_ITEM, {
                    serial: scan, title: '', isbn: '', status: 'loading',
                } );
            }

            try {
                await dispatch( actionTypes.verifyScan, scan );
                await dispatch( actionTypes.processScan, scan );
                await AudioUtil.playSound( soundNames.SUCCESS_SHORT );
            } catch ( e ) {
                await dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( e ),
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                commit( mutationTypes.REMOVE_SCANNED_ITEM, {
                    serial: scan,
                } );
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
            }
        },
    },
    mutations: {
        [ mutationTypes.SET_DESTINATION_LOCATION ]( state, { destinationLocation } ) {
            state.destinationLocation = destinationLocation;
        },
        [ mutationTypes.SET_EXPECTED_RECEIVE_DATE ]( state, { expectedReceiveDate } ) {
            state.expectedReceiveDate = expectedReceiveDate;
        },
        [ mutationTypes.SET_SCAN_STRING ]( state, { scanString } ) {
            state.scanString = scanString;
        },
        [ mutationTypes.SET_IN_FLIGHT ]( state, { inFlight } ) {
            state.inFlight = inFlight;
        },
        [ mutationTypes.PUSH_SCANNED_ITEM ]( state, scannedItem ) {
            state.scannedItems = [ scannedItem, ...state.scannedItems ];
        },
        [ mutationTypes.SET_SCANNED_ITEM ]( state, {
            serial,
            title,
            isbn,
            status,
            message,
        } ) {
            const itemToChange = state.scannedItems.find( ( item ) => (
                ( item.serial.toString().toLowerCase() === serial.toString().toLowerCase() )
                || ( item.serial.toString().toLowerCase() === `id${ serial.toString().toLowerCase() }` )
            ) );
            itemToChange.serial = serial.toString().toLowerCase().startsWith( 'id' )
                ? serial
                : `ID${ serial }`;
            itemToChange.title = title || itemToChange.title || '';
            itemToChange.isbn = isbn || itemToChange.isbn || '';
            itemToChange.status = status || itemToChange.status || '';
            itemToChange.message = message || '';
        },
        [ mutationTypes.REMOVE_SCANNED_ITEM ]( state, { serial } ) {
            state.scannedItems = state.scannedItems.filter( ( item ) => item.serial !== serial );
        },
        [ mutationTypes.RESET_SCANNED_ITEMS ]( state ) {
            state.scannedItems = [];
        },
    },
};
